import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

const CATEGORIES = [
  { value: 'Rent', icon: 'fa-building', color: 'bg-blue-100 text-blue-700' },
  { value: 'Utilities', icon: 'fa-bolt', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'Supplies', icon: 'fa-box', color: 'bg-purple-100 text-purple-700' },
  { value: 'Fuel', icon: 'fa-gas-pump', color: 'bg-red-100 text-red-700' },
  { value: 'Internet', icon: 'fa-wifi', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'Software', icon: 'fa-laptop-code', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'Other', icon: 'fa-receipt', color: 'bg-gray-100 text-gray-700' },
];

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const Expenses = ({ embedded = false }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Other');
  const [vendor, setVendor] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [showOptional, setShowOptional] = useState(false);

  // Filter
  const now = new Date();
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1); // 1-12, 0 = All months

  // Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterYear) params.year = filterYear;
      if (filterMonth) params.month = filterMonth;
      const res = await adminApi.get('/expenses', { params });
      setExpenses(res.data || []);
    } catch (err) {
      console.error('Fetch expenses error', err);
      toast.error('Could not load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [filterYear, filterMonth]); // eslint-disable-line

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setImageFile(file);
    setImageUrl('');
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const res = await adminApi.post('/expenses/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.image_url || '';
      setImageUrl(url);
      return url;
    } catch (err) {
      console.error('Upload error', err);
      toast.error('Image upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
    setAmount('');
    setCategory('Other');
    setVendor('');
    setNote('');
    setDate(new Date().toISOString().slice(0, 10));
    setShowOptional(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      let finalImageUrl = imageUrl;
      if (imageFile && !imageUrl) {
        finalImageUrl = await uploadImage();
        if (!finalImageUrl && imageFile) {
          setSubmitting(false);
          return; // upload failed
        }
      }
      await adminApi.post('/expenses', {
        amount: amt,
        date,
        vendor: vendor.trim(),
        category,
        note: note.trim(),
        image_url: finalImageUrl || '',
      });
      toast.success('Expense saved');
      resetForm();
      fetchExpenses();
    } catch (err) {
      console.error('Save expense error', err);
      toast.error('Could not save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await adminApi.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      setExpenses((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      console.error('Delete error', err);
      toast.error('Could not delete');
    }
  };

  const totalAmount = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  const years = (() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  })();

  const getCategoryStyle = (cat) => {
    const c = CATEGORIES.find((x) => x.value === cat);
    return c || CATEGORIES[CATEGORIES.length - 1];
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4'}>
      <div className={embedded ? '' : 'max-w-6xl mx-auto'}>
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          {!embedded && (
            <button
              onClick={() => navigate('/staff')}
              className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
              data-testid="back-to-admin-btn"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
          )}

          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-xl shadow-md px-5 py-3 border border-gray-100" data-testid="expenses-total-card">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
                {filterMonth ? `${monthNames[filterMonth - 1]} ${filterYear}` : `${filterYear}`} Total
              </p>
              <p className="text-2xl font-bold text-emerald-600">${totalAmount.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{expenses.length} {expenses.length === 1 ? 'receipt' : 'receipts'}</p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <i className="fas fa-receipt text-emerald-600"></i>
            Bill & Expense Tracker
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Snap a photo, type the amount — your tax records, sorted.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Upload Form */}
          <div className="lg:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6"
              data-testid="expense-form"
            >
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="fas fa-plus-circle text-emerald-600"></i>
                New Expense
              </h2>

              {/* Image upload */}
              <div className="mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="expense-file-input"
                />
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-emerald-200">
                    <img src={imagePreview} alt="Receipt preview" className="w-full h-56 object-contain bg-gray-50" />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                        setImageUrl('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md text-gray-600 hover:text-red-600"
                      data-testid="remove-image-btn"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-10 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-700 hover:bg-emerald-50 transition-all flex flex-col items-center gap-2"
                    data-testid="snap-receipt-btn"
                  >
                    <i className="fas fa-camera text-4xl"></i>
                    <span className="font-semibold">Snap Receipt</span>
                    <span className="text-xs text-gray-400">or tap to choose a photo</span>
                  </button>
                )}
              </div>

              {/* Amount */}
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    required
                    className="w-full pl-9 pr-4 py-3 text-2xl font-bold text-gray-800 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                    data-testid="expense-amount-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Category</label>
                <div className="grid grid-cols-2 gap-2" data-testid="expense-category-group">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCategory(c.value)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all border-2 ${
                        category === c.value
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`category-${c.value.toLowerCase()}-btn`}
                    >
                      <i className={`fas ${c.icon}`}></i>
                      {c.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                  data-testid="expense-date-input"
                />
              </div>

              {/* Optional fields toggle */}
              <button
                type="button"
                onClick={() => setShowOptional((v) => !v)}
                className="w-full text-left text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-2"
                data-testid="toggle-optional-btn"
              >
                <i className={`fas fa-chevron-${showOptional ? 'down' : 'right'} text-xs`}></i>
                Optional details (vendor, note)
              </button>

              {showOptional && (
                <div className="space-y-3 mb-4">
                  <input
                    type="text"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="Vendor (e.g. Shell, Albert Heijn)"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm"
                    data-testid="expense-vendor-input"
                  />
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note (optional)"
                    rows="2"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-sm resize-none"
                    data-testid="expense-note-input"
                  />
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || uploading}
                className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="save-expense-btn"
              >
                {submitting || uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {uploading ? 'Uploading...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Expense
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Expense List */}
          <div className="lg:col-span-3">
            {/* Filter Bar */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <i className="fas fa-filter text-gray-400"></i>
                <span className="text-sm font-semibold text-gray-600">Filter:</span>
              </div>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(parseInt(e.target.value))}
                className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="filter-year-select"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(parseInt(e.target.value))}
                className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="filter-month-select"
              >
                <option value={0}>All months</option>
                {monthNames.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <button
                onClick={fetchExpenses}
                className="ml-auto px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                data-testid="refresh-expenses-btn"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>

            {/* List */}
            {loading ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center text-gray-400">
                <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
                <p>Loading expenses...</p>
              </div>
            ) : expenses.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center" data-testid="empty-expenses">
                <i className="fas fa-receipt text-5xl text-gray-300 mb-3"></i>
                <p className="text-gray-500 font-semibold">No expenses yet</p>
                <p className="text-gray-400 text-sm mt-1">Snap your first receipt to get started.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="expenses-list">
                {expenses.map((exp) => {
                  const catStyle = getCategoryStyle(exp.category);
                  return (
                    <div
                      key={exp.id}
                      className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 flex items-center gap-4 hover:shadow-lg transition-all"
                      data-testid={`expense-row-${exp.id}`}
                    >
                      {/* Thumbnail */}
                      {exp.image_url ? (
                        <button
                          onClick={() => setLightboxImage(exp.image_url)}
                          className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 hover:ring-2 hover:ring-emerald-400"
                          data-testid={`expense-thumb-${exp.id}`}
                        >
                          <img src={exp.image_url} alt="Receipt" className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-300">
                          <i className="fas fa-image text-2xl"></i>
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${catStyle.color}`}>
                            <i className={`fas ${catStyle.icon}`}></i>
                            {exp.category}
                          </span>
                          <span className="text-xs text-gray-400">{exp.date}</span>
                        </div>
                        {exp.vendor && <p className="text-sm font-semibold text-gray-700 truncate">{exp.vendor}</p>}
                        {exp.note && <p className="text-xs text-gray-500 truncate">{exp.note}</p>}
                      </div>

                      {/* Amount + Delete */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl font-bold text-gray-800">${parseFloat(exp.amount).toFixed(2)}</p>
                        <button
                          onClick={() => handleDelete(exp.id)}
                          className="text-xs text-gray-400 hover:text-red-500 mt-1"
                          data-testid={`delete-expense-${exp.id}`}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setLightboxImage(null)}
          data-testid="expense-lightbox"
        >
          <button
            className="absolute top-6 right-6 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center"
            onClick={() => setLightboxImage(null)}
          >
            <i className="fas fa-times text-xl"></i>
          </button>
          <img src={lightboxImage} alt="Receipt" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default Expenses;
